import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  categoryGroupsApi,
  CATEGORY_GROUP_TABS,
  GroupMetrics,
  GroupCategoryRow
} from '@/api/categoryGroups.api';
import { ticketsApi } from '@/api/tickets.api';
import { TicketTable } from '@/components/tickets/TicketTable';
import { TicketFiltersBar } from '@/components/tickets/TicketFiltersBar';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useRealtime } from '@/context/RealtimeContext';
import { useFiltersInUrl } from '@/hooks/useFiltersInUrl';
import { CATEGORY_LABEL } from '@/types/domain';

/**
 * V4.9.25 — Shared dashboard for a category group (Refund / Reprint &
 * Replacement / Other issues / Request). Two modes, both ON THE SAME ROUTE so
 * the sidebar tab stays active (you never get bounced to "All Tickets"):
 *   • Overview — per-sub-category stat cards + geo boxes + geo drill-down.
 *   • List     — when a sub-category / "See all" is clicked, the filtered
 *                tickets render here with the full filter bar (category locked
 *                to this tab) and a "Back to overview" link.
 * Additive; nothing existing changes.
 */
export function CategoryGroupPage({ groupKey }: { groupKey: string }) {
  const tab = CATEGORY_GROUP_TABS.find((t) => t.key === groupKey);
  const codes = tab?.codes ?? [];
  const path = tab?.path ?? '/';

  const [query, setQuery] = useFiltersInUrl();
  const [selectedGeo, setSelectedGeo] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { on } = useRealtime();

  // Only categories that belong to THIS tab count (can't escape the tab).
  const activeCats = useMemo(
    () => ((query.category as string[]) ?? []).filter((c) => codes.includes(c)),
    [query.category, codes]
  );
  const listMode = activeCats.length > 0;

  // Live updates in both modes (same signals the dashboard/All-Tickets use).
  useEffect(() => {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['category-group', groupKey] });
    };
    const offs = (
      ['ticket.created', 'ticket.status_changed', 'ticket.updated', 'ticket.approval_decided', 'ticket.cx_informed'] as const
    ).map((e) => on(e, invalidate));
    return () => offs.forEach((f) => f());
  }, [on, queryClient, groupKey]);

  // Overview data (disabled while drilled into the list).
  const statsQ = useQuery({
    queryKey: ['category-group', groupKey],
    queryFn: () => categoryGroupsApi.stats(groupKey),
    enabled: !listMode,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000
  });

  // List data — always scoped to this tab's categories.
  const effectiveCategory = activeCats.length ? activeCats : codes;
  const listQuery = { ...query, category: effectiveCategory };
  const listQ = useQuery({
    queryKey: ['tickets', groupKey, listQuery],
    queryFn: () => ticketsApi.list(listQuery),
    enabled: listMode,
    placeholderData: (prev) => prev
  });

  // ---------- LIST MODE (filtered tickets, in-tab) ----------
  if (listMode) {
    const d = listQ.data;
    const total = d?.pagination.total ?? 0;
    const page = d?.pagination.page ?? 1;
    const perPage = d?.pagination.per_page ?? 25;
    const totalPages = d?.pagination.total_pages ?? 1;
    const allActive = activeCats.length === codes.length;

    return (
      <div>
        <div className="section-header">
          <h2>
            {tab?.title ?? groupKey}
            {tab?.subtitle ? <span className="text-small text-muted" style={{ fontWeight: 500, marginLeft: 8 }}>· {tab.subtitle}</span> : null}
            {total > 0 ? <span className="text-muted text-small"> · {total}</span> : null}
          </h2>
          <Link to={path} className="btn btn-secondary btn-sm">← Back to overview</Link>
        </div>

        {/* Sub-category focus chips — switch WITHOUT leaving the tab. */}
        <div className="flex gap-2" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
          <button type="button" className={`btn btn-sm ${allActive ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setQuery((p) => ({ ...p, category: codes, page: 1 }))}>
            All
          </button>
          {codes.map((code) => {
            const on1 = activeCats.length === 1 && activeCats[0] === code;
            return (
              <button key={code} type="button" className={`btn btn-sm ${on1 ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setQuery((p) => ({ ...p, category: [code], page: 1 }))}>
                {CATEGORY_LABEL[code as keyof typeof CATEGORY_LABEL] ?? code}
              </button>
            );
          })}
        </div>

        <div className="card card-overflow-visible">
          <TicketFiltersBar value={query} onChange={setQuery} hideCategory />
          {listQ.isLoading ? (
            <LoadingState label="Loading tickets..." />
          ) : listQ.isError ? (
            <ErrorState error={listQ.error} onRetry={() => listQ.refetch()} />
          ) : (
            <>
              <TicketTable tickets={d?.data ?? []} />
              <div className="pagination">
                <div className="text-small text-muted">Page {page} of {totalPages} · {total} tickets</div>
                <div className="flex gap-2">
                  <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1}
                    onClick={() => setQuery((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}>Previous</button>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={page >= totalPages}
                    onClick={() => setQuery((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}>Next</button>
                  <select className="form-select" style={{ width: 'auto' }} value={perPage}
                    onChange={(e) => setQuery((p) => ({ ...p, per_page: Number(e.target.value), page: 1 }))}>
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---------- OVERVIEW MODE (boxes) ----------
  if (statsQ.isLoading) return <LoadingState label="Loading…" large />;
  if (statsQ.isError) return <ErrorState error={statsQ.error} onRetry={() => statsQ.refetch()} />;
  const data = statsQ.data;
  if (!data) return null;

  const codesCsv = data.categories.map((c) => c.code).join(',');
  const toList = (categoryCsv: string, storeCodes?: string[]) => {
    const p = new URLSearchParams();
    p.set('category', categoryCsv);
    if (storeCodes && storeCodes.length) p.set('store', storeCodes.join(','));
    p.set('open', '1');
    return `${path}?${p.toString()}`;
  };
  const selected = selectedGeo ? data.geos.find((g) => g.geo === selectedGeo) ?? null : null;

  return (
    <div>
      <div className="section-header">
        <h2>
          {tab?.title ?? data.group}
          {tab?.subtitle ? <span className="text-small text-muted" style={{ fontWeight: 500, marginLeft: 8 }}>· {tab.subtitle}</span> : null}
        </h2>
      </div>

      {/* Top stats, split BY sub-category (shine = entry points). */}
      <div className="subtype-grid mt-4">
        {data.by_category.map((c) => (
          <Link key={c.code} to={toList(c.code)} className="subtype-card bds-shine bds-shine-soft">
            <div className="subtype-card-title">{c.label}</div>
            <MetricRow m={c} />
          </Link>
        ))}
      </div>

      {/* Geo boxes — click to drill into that region. */}
      <h3 className="card-title" style={{ margin: '22px 0 10px' }}>By region</h3>
      <div className="geo-breakdown-grid">
        {data.by_geo.map((g) => (
          <div key={g.geo} className={`geo-card${selectedGeo === g.geo ? ' is-selected' : ''}`} role="button" tabIndex={0}
            onClick={() => setSelectedGeo((cur) => (cur === g.geo ? null : g.geo))}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedGeo((cur) => (cur === g.geo ? null : g.geo)); }}
            style={{ cursor: 'pointer' }}>
            <div className="geo-card-header">{g.label}</div>
            <div className="geo-card-stats">
              <div className="geo-stat"><div className="geo-card-num">{g.open}</div><div className="geo-card-sub">Open</div></div>
              <div className="geo-stat"><div className="geo-card-num" style={{ color: g.breached ? '#dc2626' : undefined }}>{g.breached}</div><div className="geo-card-sub">Breached</div></div>
              <div className="geo-stat"><div className="geo-card-num" style={{ color: g.approvals_waiting ? '#d97706' : undefined }}>{g.approvals_waiting}</div><div className="geo-card-sub">Approvals</div></div>
            </div>
            <div className="text-small" style={{ marginTop: 10, color: '#6366f1', fontWeight: 600 }}>
              {selectedGeo === g.geo ? 'Hide breakdown ▲' : 'View breakdown ▼'}
            </div>
          </div>
        ))}
      </div>

      {selected ? (
        <div className="card mt-4">
          <div className="card-header">
            <h3 className="card-title">{selected.label} — breakdown</h3>
            <Link to={toList(codesCsv, selected.store_codes)} className="text-small" style={{ color: '#6366f1', fontWeight: 600 }}>See all open →</Link>
          </div>
          <div className="card-body">
            {data.categories.map((cat) => {
              const m = data.matrix[selected.geo]?.[cat.code];
              if (!m) return null;
              return (
                <div key={cat.code} className="geo-detail-row">
                  <div style={{ fontWeight: 600 }}>{cat.label}</div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'baseline' }}>
                    <Mini n={m.open} l="Open" />
                    <Mini n={m.breached} l="Breached" tone={m.breached ? 'warn' : undefined} />
                    <Mini n={m.under_24h} l="Due within 24h" tone={m.under_24h ? 'amber' : undefined} />
                    <Mini n={m.approvals_waiting} l="Appr." tone={m.approvals_waiting ? 'amber' : undefined} />
                    <Link to={toList(cat.code, selected.store_codes)} className="text-small" style={{ color: '#6366f1', fontWeight: 600 }}>See all →</Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-small text-muted" style={{ marginTop: 12 }}>
          Tip: click a region above to see its {data.categories.map((c) => c.label).join(' / ')} breakdown.
        </p>
      )}
    </div>
  );
}

function MetricRow({ m }: { m: GroupMetrics | GroupCategoryRow }) {
  return (
    <div className="subtype-metrics">
      <div className="subtype-metric"><span className="num">{m.open}</span><span className="lbl">Open</span></div>
      <div className={`subtype-metric${m.breached ? ' warn' : ''}`}><span className="num">{m.breached}</span><span className="lbl">Breached</span></div>
      <div className={`subtype-metric${m.under_24h ? ' amber' : ''}`}><span className="num">{m.under_24h}</span><span className="lbl">Due within 24h</span></div>
      <div className={`subtype-metric${m.approvals_waiting ? ' amber' : ''}`}><span className="num">{m.approvals_waiting}</span><span className="lbl">Approvals</span></div>
      <div className="subtype-metric"><span className="num">{m.created_today}</span><span className="lbl">Today</span></div>
    </div>
  );
}

function Mini({ n, l, tone }: { n: number; l: string; tone?: 'warn' | 'amber' }) {
  const color = tone === 'warn' ? '#dc2626' : tone === 'amber' ? '#d97706' : undefined;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <strong style={{ fontSize: 16, color }}>{n}</strong>
      <span className="text-small text-muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>{l}</span>
    </span>
  );
}
